import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setErrorMessage, setSuccessMessage } from "../slices/messageSlice";
import { getAllUsers, updateUserInfo } from "../services/UserService";
import UserPopup from "./UserPopup";
import * as XLSX from "xlsx";
import { UpdateSales } from "../services/SaleService";
import UserActions from "./users/UserList/UserActions";
import UserTable from "./users/UserList/UserTable";
import UserPagination from "./users/UserList/UserPagination";
import UserFilters from "./users/UserList/UserFilters";

const ITEMS_PER_PAGE = 20;
const maxPageNumbersToShow = 5;
const UserList = () => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    firstName: "",
    phoneNumber: "",
    code: "",
    firstLoginAtFrom: "",
    firstLoginAtTo: "",
    loginStatus: "",
    createdAtFrom: "",
    createdAtTo: "",
    id: "",
    datePostAtFrom: "",
    datePostAtTo: "",
    lastLoginAtFrom: "",
    lastLoginAtTo: "",
  });
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getAllUsers(
        page,
        ITEMS_PER_PAGE,
        filter.id,
        filter.firstName,
        filter.phoneNumber,
        filter.code,
        formatDate(filter.firstLoginAtFrom),
        formatDate(filter.firstLoginAtTo,true),
        formatDate(filter.createdAtFrom),
        formatDate(filter.createdAtTo,true),
        formatDate(filter.datePostAtFrom),
        formatDate(filter.datePostAtTo,true),
        formatDate(filter.lastLoginAtFrom),
        formatDate(filter.lastLoginAtTo,true)
      );
      if (response.status_code === 200) {
        if (
          response.data.data === "[]" ||
          typeof response.data.data == String
        ) {
          setUserData([]);
          setTotalPages(0);
          setPage(0);
          setTotalUsers(0);
        } else {
          setUserData(response.data.data);
          setTotalPages(response.data.page_max);
          setTotalUsers(response.data.totals);
        }
      } else {
        dispatch(setErrorMessage(response.message));
      }
    } catch (error) {
      dispatch(setErrorMessage("Error fetching users:", error));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString, isEnd =false) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    
    const pad = (num) => String(num).padStart(2, '0');
  
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());

    if (isEnd) {
      date.setDate(date.getDate() + 1);
      return `${year}-${month}-${day} 23:59:59`;
    }
    return `${year}-${month}-${day} 00:00:00`;
  };

  useEffect(() => {
    fetchUsers();
  }, [filter, page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setPage(1);
    setFilter((prev) => {
      const newFilter = { ...prev, [name]: value };

      if (name === "loginStatus") {
        const currentYear = new Date().getFullYear();
        if (value === "loggedIn") {
          newFilter.firstLoginAtFrom = `${currentYear}-01-01T00:00`;
          newFilter.firstLoginAtTo =
            new Date().toISOString().slice(0, 10) + "T23:59";
        } else if (value === "notLoggedIn") {
          newFilter.firstLoginAtFrom = `${currentYear - 1}-01-01T00:00`;
          newFilter.firstLoginAtTo = `${currentYear - 1}-12-31T23:59`;
        } else {
          newFilter.firstLoginAtFrom = "";
          newFilter.firstLoginAtTo = "";
        }
      }
      return newFilter;
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser({
      ...user,
      birthDay: user.birthDay ? user.birthDay.split(" ")[0] : "",
    });
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedUser(null);
  };

  const handleUpdateUser = async (selectedUser) => {
    const response = await updateUserInfo(
      selectedUser.id,
      selectedUser.firstName,
      selectedUser.lastName,
      selectedUser.address,
      selectedUser.email,
      selectedUser.birthDay,
      selectedUser.contactPerson,
      selectedUser.status,
      selectedUser.totalSales
    );
    if (response.status_code == 200) {
      fetchUsers();
      dispatch(setSuccessMessage("Cập nhật thông tin người dùng thành công!"));
    } else {
      dispatch(setErrorMessage(response.message));
    }
    closePopup();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const formattedData = jsonData
        .slice(1)
        .map((row) => {
          if (row.length >= 2) {
            return `${row[1]}-${row[0]}`;
          }
          return null;
        })
        .filter((item) => item !== null);

      const joinedData = formattedData.join(";;");
      const response = await UpdateSales(joinedData);
      if (response.status_code === 200) {
        fetchUsers();
        dispatch(setSuccessMessage("Cập nhật doanh thu thành công!"));
      } else {
        dispatch(setErrorMessage(response.message));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadList = async () => {
    setIsDownloading(true);
    const response = await getAllUsers(
      1,
      totalUsers,
      filter.id,
      filter.firstName,
      filter.phoneNumber,
      filter.code,
      formatDate(filter.firstLoginAtFrom),
      formatDate(filter.firstLoginAtTo,true),
      formatDate(filter.createdAtFrom),
      formatDate(filter.createdAtTo,true),
      formatDate(filter.datePostAtFrom),
      formatDate(filter.datePostAtTo,true),
      formatDate(filter.lastLoginAtFrom),
      formatDate(filter.lastLoginAtTo,true)
    );
    if (response.status_code === 200) {
      const data = response.data.data;
      const excelData = data.map((user, index) => ({
        "Doanh Thu": user.totalSales ? user.totalSales : 0,
        ID: user.id,
        "Mã KH": user.code,
        Họ: user.lastName,
        Tên: user.firstName,
        "Số điện thoại": user.phoneNumber,
        "Số bài đăng": user?.detailNumPosts?.totalPosts,
        Email: user.email,
        "Người liên hệ": user.contactPerson,
        "Ngày tạo": user.createdAt
          ? new Date(user.createdAt).toLocaleString()
          : "",
        "Đăng nhập lần đầu": user.firstLoginAt
          ? new Date(user.firstLoginAt).toLocaleString()
          : "",
        "Đăng nhập lần cuối": user.currentLoginAt
          ? new Date(user.currentLoginAt).toLocaleString()
          : "",
      }));

      // Tạo workbook mới
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Điều chỉnh độ rộng cột
      const colWidths = [
        { wch: 10 }, //Doanh Thu
        { wch: 10 }, // ID
        { wch: 15 }, // Mã KH
        { wch: 20 }, // Họ
        { wch: 20 }, // Tên
        { wch: 15 }, // Số điện thoại
        { wch: 15 }, // Tổng số bài đăng
        { wch: 30 }, // Email
        { wch: 20 }, // Người liên hệ
        { wch: 20 }, // Ngày tạo
        { wch: 20 }, // Đăng nhập lần đầu
      ];
      ws["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Danh sách người dùng");
      const fileName = `danh_sach_nguoi_dung_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } else {
      dispatch(setErrorMessage(response.message));
    }

    setIsDownloading(false);
  };

  return (
    <div className="container min-h-screen h-full mx-auto p-4 dark:p-8 bg-white text-black dark:bg-gray-900 dark:text-white dark:border dark:rounded-xl dark:border-gray-500 ">
      <h1 className="text-3xl font-bold mb-4">DANH SÁCH NGƯỜI DÙNG</h1>

      <div className="flex justify-between items-start mb-8">
        <UserFilters
          filter={filter}
          handleFilterChange={handleFilterChange}
          setFilter={setFilter}
        />

        <UserActions
          onDownload={handleDownloadList}
          onFileUpload={handleFileUpload}
          isDownloading={isDownloading}
        />
      </div>
      <UserTable
        users={userData}
        onUserClick={handleUserClick}
        loading={loading}
      />
      <UserPagination
        currentPage={page}
        totalPages={totalPages}
        totalUsers={totalUsers}
        onPageChange={handlePageChange}
        maxPageNumbersToShow={7}
        itemsPerPage={ITEMS_PER_PAGE}
      />

      {isPopupOpen && selectedUser && (
        <UserPopup
          selectedUser={selectedUser}
          onUpdate={handleUpdateUser}
          onClose={closePopup}
        />
      )}
    </div>
  );
};

export default UserList;
